import {
  Address,
  AloftDate,
  AloftPrice,
  AloftTime,
  InspectionData,
  PropertyTypes,
  schAloftDate,
  schFilledAddress,
  schMainPropertyType,
  schXMLPropertyResult,
} from '@aloft/toolkit-shared';

import { z } from 'zod';

export enum OrderTag {
  Overdue = 'Overdue',
  ROV = 'ROV',
  Revision = 'Revision',
  Mock = 'Demo',
}

/**
 * Used to support both v1 and v2 orders in the same OrdersTable.
 * Eventually we will filter out v1 orders and drop the 'legacy' value from `type`.
 */
export type TkOrderListing = {
  id: string;
  type: 'tkv2' | 'legacy';
  toggleArchive: (isArchived: boolean) => void;
  deleteOrder: () => void;
  downloadReport: () => void;
  openDocumentsPage: () => void;
} & Partial<{
  address: Address;
  createdAt: AloftTime;
  effectiveDate: AloftDate;
  orderNumber: string;
  propertyType: PropertyTypes;
  isArchived: boolean;
  isCraftsman: boolean;
  isTraining: boolean;
  requiresPrefill: boolean;
  appraisalType: string;
  clientName: string;
  appraiserFee: AloftPrice;
  appraisifyFee: AloftPrice;
  dueDate: AloftDate;
  inspectionDate: AloftDate;
  appraiserInstructions: string;
  intendedUsers: string;
  intendedUse: string;
  inspectorType: string;
  inspectifyOrder: boolean;
  borrowerName: string;
  tags: OrderTag[];
  isCompleted: boolean;
  signedAt: AloftTime;
  inspectionData: InspectionData;
  isRevision: boolean;
  isMock: boolean;
}>;

export type TkOrderListfield = keyof Pick<
  TkOrderListing,
  'address' | 'effectiveDate' | 'propertyType' | 'appraisalType' | 'clientName' | 'tags'
>;

export type Tkv2SortState = {
  field: TkOrderListfield;
  dir: 'asc' | 'desc';
};

export const schCraftsmanCreateOrderForm = z.object({
  createBy: z.enum(['XML', 'Address']),
  // All Craftsman orders have address, effectiveDate, and property type
  address: schFilledAddress,
  effectiveDate: schAloftDate,
  propertyType: schMainPropertyType,
  orderNumber: z.string().optional(),
  inspectifyOrder: z.boolean().optional(),
  options: z
    .object({
      costToCure: z.boolean().optional(),
      arv: z.boolean().optional(),
    })
    .optional(),
  mock: z.boolean().optional(),
  inspectionDate: schAloftDate.optional(),
  dueDate: schAloftDate.optional(),
  clientName: z.string().optional(),
  appraisalType: z.string().optional(),
  appraiserFee: z.number().min(0).optional(),
  appraisifyFee: z.number().min(0).optional(),
  appraiserInstructions: z.string().optional(),
  borrowerName: z.string().optional(),
  intendedUse: z.string().optional(),
  inspectorType: z.string().optional(),
  reportId: z.string().optional(),
  // but orders created from XML will may also have the following imported fields
  // if an import is performed we would always require the xml file itself
  imported: z
    .object({
      xml: z.instanceof(File),
      subject: schXMLPropertyResult.optional(),
      comps: z.array(schXMLPropertyResult).optional(),
    })
    .optional(),
});
export type CraftsmanCreateOrderForm = z.infer<typeof schCraftsmanCreateOrderForm>;
